// src/pages/TCC/TCCHomePage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Container, Grid, Typography, Card, CardContent, Button, Chip,
  Stack, Avatar, Divider, Paper, LinearProgress, Accordion,
  AccordionSummary, AccordionDetails, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  LibraryBooks as LibraryBooksIcon,
  Gavel as GavelIcon,
  Star as StarIcon,
  Archive as ArchiveIcon,
  Storage as StorageIcon,
  FindInPage as FindInPageIcon,
  ArrowForward as ArrowForwardIcon,
  BarChart as BarChartIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';

// ── Etapas do fluxo ───────────────────────────────────────────────────────────
const ETAPAS = [
  {
    numero: '01',
    label: 'Proposta',
    desc: 'O estudante submete a proposta de tema. O Chefe de Departamento aprova ou rejeita.',
    icon: <AssignmentIcon sx={{ fontSize: 36 }} />,
    color: '#1976d2',
    bg: '#e3f2fd',
    path: '/propostas',
    roles: ['Estudante', 'Chefe Departamento'],
  },
  {
    numero: '02',
    label: 'Projecto',
    desc: 'Desenvolvido com o Supervisor. Inclui verificação Anti-Plágio/IA pelo Chefe de Departamento.',
    icon: <DescriptionIcon sx={{ fontSize: 36 }} />,
    color: '#388e3c',
    bg: '#e8f5e9',
    path: '/projetos',
    roles: ['Estudante', 'Supervisor', 'Chefe Departamento'],
    hasPlagio: true,
  },
  {
    numero: '03',
    label: 'Monografia',
    desc: 'Submissão final. Revisão pelo Revisor e nova verificação Anti-Plágio/IA antes da defesa.',
    icon: <LibraryBooksIcon sx={{ fontSize: 36 }} />,
    color: '#7b1fa2',
    bg: '#f3e5f5',
    path: '/monografias',
    roles: ['Estudante', 'Revisor', 'Chefe Departamento'],
    hasPlagio: true,
  },
  {
    numero: '04',
    label: 'Defesa',
    desc: 'Defesa pública perante o júri: Presidente, Arguente e Supervisor.',
    icon: <GavelIcon sx={{ fontSize: 36 }} />,
    color: '#e65100',
    bg: '#fff3e0',
    path: '/defesas',
    roles: ['Estudante', 'Presidente', 'Arguente', 'Supervisor'],
  },
  {
    numero: '05',
    label: 'Avaliação',
    desc: 'O júri atribui a nota final. O resultado fica registado no sistema.',
    icon: <StarIcon sx={{ fontSize: 36 }} />,
    color: '#f9a825',
    bg: '#fffde7',
    path: '/avaliacoes',
    roles: ['Presidente', 'Arguente', 'Supervisor'],
  },
  {
    numero: '06',
    label: 'Arquivamento',
    desc: 'A monografia aprovada é arquivada com todos os metadados e documentos associados.',
    icon: <ArchiveIcon sx={{ fontSize: 36 }} />,
    color: '#00796b',
    bg: '#e0f2f1',
    path: '/monografias',
    roles: ['Chefe Departamento', 'Instituição'],
  },
  {
    numero: '07',
    label: 'Repositório',
    desc: 'Publicação no Repositório Digital para consulta pública ou institucional.',
    icon: <StorageIcon sx={{ fontSize: 36 }} />,
    color: '#5d4037',
    bg: '#efebe9',
    path: '/repositorio',
    roles: ['Chefe Departamento', 'Instituição'],
  },
];

// ── Intervenientes ────────────────────────────────────────────────────────────
const INTERVENIENTES = [
  {
    role: 'Estudante',
    icon: <SchoolIcon />,
    color: '#1976d2',
    etapas: '01 → 04',
    desc: 'Submete Proposta, Projecto e Monografia. Defende o trabalho perante o júri.',
  },
  {
    role: 'Supervisor',
    icon: <PersonIcon />,
    color: '#388e3c',
    etapas: '02 → 04',
    desc: 'Orienta o estudante no Projecto e Monografia. Participa na Defesa como membro do júri.',
  },
  {
    role: 'Revisor do Projecto',
    icon: <FindInPageIcon />,
    color: '#7b1fa2',
    etapas: '03',
    desc: 'Analisa a Monografia antes da defesa, emite parecer e verifica conformidade.',
  },
  {
    role: 'Arguente',
    icon: <RecordVoiceOverIcon />,
    color: '#e65100',
    etapas: '04 → 05',
    desc: 'Questiona o estudante na Defesa e atribui nota como membro do júri.',
  },
  {
    role: 'Presidente do Júri',
    icon: <GavelIcon />,
    color: '#c62828',
    etapas: '04 → 05',
    desc: 'Preside à Defesa, coordena o júri e regista a avaliação final.',
  },
  {
    role: 'Chefe de Departamento',
    icon: <SupervisorAccountIcon />,
    color: '#00796b',
    etapas: '01 → 06',
    desc: 'Aprova propostas, desencadeia verificações Anti-Plágio/IA, gere o processo geral.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────

export default function TCCHomePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotas, setQuotas] = useState(null);

  const fetchQuotas = useCallback(async () => {
    if (!user?.instituicaoId) return;
    try {
      const res = await api.get(`/billing/quotas?instituicao_id=${user.instituicaoId}`);
      setQuotas(res.data);
    } catch {
      // quotas opcionais
    }
  }, [user?.instituicaoId]);

  useEffect(() => { fetchQuotas(); }, [fetchQuotas]);

  const plagioPercent = quotas
    ? Math.round(((quotas.quota_plagio_usado || 0) / (quotas.quota_plagio_limite || 1)) * 100)
    : null;

  return (
    <Box>

      {/* ── Hero ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #4a148c 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 64, height: 64 }}>
                  <LibraryBooksIcon sx={{ fontSize: 36 }} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight={800}>
                    TCC & Plágio/IA
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.8 }}>
                    Gestão completa do ciclo académico de investigação
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="h6" sx={{ mb: 4, fontWeight: 300, opacity: 0.9, lineHeight: 1.7 }}>
                Da proposta de tema à publicação no repositório — todo o fluxo numa única plataforma,
                com detecção de plágio e IA integrada nas etapas críticas.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/propostas"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: 'white', color: '#1a237e', fontWeight: 700, px: 4,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
                >
                  Ver Propostas
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/dashboard/tcc"
                  startIcon={<BarChartIcon />}
                  sx={{ borderColor: 'white', color: 'white', fontWeight: 700, px: 4,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  Analytics TCC
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                sx={{
                  p: 3, bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 3,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Fluxo em 7 etapas
                </Typography>
                {ETAPAS.map((e, idx) => (
                  <Box key={e.label} sx={{ display: 'flex', alignItems: 'center', mb: idx < 6 ? 1.5 : 0 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: e.color, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>
                      <Typography variant="caption" fontWeight={700} color="white">{e.numero}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>{e.label}</Typography>
                    {e.hasPlagio && (
                      <Chip label="Anti-Plágio/IA" size="small"
                        sx={{ ml: 'auto', height: 18, fontSize: '0.6rem', bgcolor: 'rgba(255,193,7,0.25)', color: '#ffe082' }} />
                    )}
                    {idx < 6 && (
                      <Box sx={{ position: 'absolute', left: 0 }} />
                    )}
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Etapas do fluxo ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Etapas do Processo
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 6 }}>
          Cada etapa tem os seus intervenientes e acções definidas. Clique para aceder.
        </Typography>

        <Grid container spacing={3}>
          {ETAPAS.map((etapa) => (
            <Grid item xs={12} sm={6} md={4} key={etapa.label}>
              <Card
                sx={{
                  height: '100%', cursor: 'pointer', border: `2px solid transparent`,
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: etapa.color, transform: 'translateY(-4px)', boxShadow: 4 },
                }}
                onClick={() => navigate(etapa.path)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Avatar sx={{ bgcolor: etapa.bg, color: etapa.color, width: 52, height: 52 }}>
                      {etapa.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        ETAPA {etapa.numero}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>{etapa.label}</Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                    {etapa.desc}
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                    {etapa.roles.map(r => (
                      <Chip key={r} label={r} size="small" variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20, borderColor: etapa.color, color: etapa.color }} />
                    ))}
                  </Stack>

                  {etapa.hasPlagio && (
                    <Chip
                      icon={<FindInPageIcon sx={{ fontSize: '14px !important' }} />}
                      label="Inclui verificação Anti-Plágio/IA"
                      size="small"
                      sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontSize: '0.65rem' }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Anti-Plágio & IA ── */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: '#fff8e1', color: '#f57f17', width: 56, height: 56 }}>
                  <FindInPageIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h4" fontWeight={700}>Anti-Plágio & IA</Typography>
              </Stack>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                A verificação de plágio e detecção de conteúdo gerado por IA é executada
                pelo <strong>Chefe de Departamento</strong> directamente nas etapas de
                <strong> Projecto</strong> e <strong>Monografia</strong>.
                Cada verificação consome uma quota do plano contratado.
              </Typography>

              <Stack spacing={2}>
                {[
                  { label: 'Acedido pelo Chefe de Departamento', icon: <CheckCircleIcon color="success" fontSize="small" /> },
                  { label: 'Disponível nas etapas de Projecto e Monografia', icon: <CheckCircleIcon color="success" fontSize="small" /> },
                  { label: 'Detecção combinada: plágio + conteúdo IA', icon: <CheckCircleIcon color="success" fontSize="small" /> },
                  { label: 'Relatório detalhado com fontes e percentagens', icon: <CheckCircleIcon color="success" fontSize="small" /> },
                ].map(item => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {item.icon}
                    <Typography variant="body2">{item.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 3 }}>
                  Quotas de Verificação
                </Typography>

                {quotas ? (
                  <Stack spacing={3}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Plágio</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {quotas.quota_plagio_usado || 0} / {quotas.quota_plagio_limite || 0} verificações
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={plagioPercent}
                        color={plagioPercent > 80 ? 'error' : plagioPercent > 60 ? 'warning' : 'success'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Detecção IA</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {quotas.quota_ia_usado || 0} / {quotas.quota_ia_limite || 0} verificações
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={quotas.quota_ia_limite
                          ? Math.round(((quotas.quota_ia_usado || 0) / quotas.quota_ia_limite) * 100)
                          : 0}
                        color="primary"
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <PsychologyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Informação de quotas disponível após activação do módulo
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Button
                  fullWidth variant="outlined" component={RouterLink}
                  to="/dashboard/tcc" startIcon={<BarChartIcon />}
                >
                  Ver Relatório Anti-Plágio/IA
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Intervenientes ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Quem participa no processo?
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 6 }}>
          Cada perfil tem acesso apenas às etapas e acções que lhe competem.
        </Typography>

        <Grid container spacing={3}>
          {INTERVENIENTES.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.role}>
              <Paper
                variant="outlined"
                sx={{ p: 3, height: '100%', borderLeft: `4px solid ${p.color}` }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: p.color, width: 40, height: 40 }}>
                    {React.cloneElement(p.icon, { sx: { fontSize: 20 } })}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>{p.role}</Typography>
                    <Chip label={`Etapas ${p.etapas}`} size="small"
                      sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${p.color}15`, color: p.color }} />
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary">{p.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── FAQ ── */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
            Perguntas Frequentes
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Dúvidas comuns sobre o módulo TCC & Plágio/IA
          </Typography>

          <Stack spacing={1.5}>
            {[
              {
                q: 'Como o estudante submete a proposta de tema?',
                a: 'O estudante acede a "Propostas" no menu, clica em "Submeter Proposta" e preenche o formulário com o tema, área de investigação e supervisor pretendido. O Chefe de Departamento recebe notificação e aprova ou rejeita.',
              },
              {
                q: 'Quando é feita a verificação Anti-Plágio/IA?',
                a: 'O Chefe de Departamento pode desencadear a verificação na página de detalhe de um Projecto ou Monografia. A verificação consome uma quota do plano e gera um relatório imediato com percentagens e fontes identificadas.',
              },
              {
                q: 'Quem agenda a defesa?',
                a: 'O agendamento é feito pelo Chefe de Departamento ou pela Direcção após a Monografia ser aprovada. Aceda a "Defesas → Agendar" e seleccione data, sala e membros do júri.',
              },
              {
                q: 'Como a monografia chega ao Repositório?',
                a: 'Após a avaliação final, a monografia pode ser arquivada e publicada no Repositório Digital. Esta acção é feita pelo Chefe de Departamento ou pela Instituição na página de detalhe da monografia.',
              },
            ].map((item) => (
              <Accordion key={item.q} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body1" fontWeight={600}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {item.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── CTA Final ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #4a148c 100%)',
          color: 'white', py: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Pronto para começar?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.85, fontWeight: 300 }}>
            Certifique-se de que os perfis dos utilizadores estão cadastrados e o módulo está activo.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained" size="large" component={RouterLink} to="/propostas"
              endIcon={<ArrowForwardIcon />}
              sx={{ bgcolor: 'white', color: '#1a237e', fontWeight: 700, px: 4, py: 1.5,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
            >
              Ver Propostas Activas
            </Button>
            <Button
              variant="outlined" size="large" component={RouterLink} to="/dashboard/tcc"
              sx={{ borderColor: 'white', color: 'white', fontWeight: 700, px: 4, py: 1.5,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Painel Analytics
            </Button>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}
